import { z } from "zod";
/*
 * The Overture document extraction ledger as a running turn uses it through the tenant API. The
 * tenant resolves each document within the run's organisation and keeps one ledger row per
 * document: a turn claims the row before it spends Reducto credits, records the submitted job, and
 * completes, skips or fails it. Each request carries the run's lease and is answered only while
 * that attempt holds the run.
 */
const id = z.string().trim().min(1).max(200);
const lease = {
    runId: id, tenantSlug: id, workerId: id,
    attemptNumber: z.number().int().positive(),
};
const documentRef = { ...lease, documentExternalId: z.string().trim().min(1).max(500) };
const revision = { ...documentRef, sourcePayloadHash: z.string().regex(/^[a-f0-9]{64}$/u) };
const fileFacts = {
    filename: z.string().nullable(),
    contentType: z.string().nullable(),
    fileSizeBytes: z.number().int().nonnegative().nullable(),
};
export const workerOvertureDocumentRequestSchema = z.discriminatedUnion("operation", [
    /** Resolves the document and returns its ledger row, if any. */
    z.object({ ...documentRef, operation: z.literal("open") }).strict(),
    z.object({ ...documentRef, operation: z.literal("read") }).strict(),
    /** Claims the row for a new submission unless a fresh one is already in flight. */
    z.object({ ...revision, operation: z.literal("claim"), ...fileFacts, staleBefore: z.number().int() }).strict(),
    z.object({ ...revision, operation: z.literal("submitted"), documentInput: z.string().min(1), jobId: id }).strict(),
    z.object({
        ...revision, operation: z.literal("completed"),
        payload: z.record(z.string(), z.unknown()), chunks: z.array(z.unknown()), extractedText: z.string(),
        usage: z.record(z.string(), z.unknown()).nullable(), numPages: z.number().int().nonnegative().nullable(),
        credits: z.number().nonnegative().nullable(), studioLink: z.string().nullable(), pdfUrl: z.string().nullable(),
    }).strict(),
    z.object({
        ...revision, operation: z.literal("skipped"), ...fileFacts,
        errorCode: z.string().trim().min(1).max(200), errorMessage: z.string(),
    }).strict(),
    z.object({ ...revision, operation: z.literal("failed"), errorMessage: z.string() }).strict(),
]);
export const workerOvertureDocumentSchema = z.object({
    externalId: z.string(),
    organisationId: id,
    payload: z.record(z.string(), z.unknown()),
    sourcePayloadHash: z.string(),
}).strict();
export const workerOvertureDocumentExtractionSchema = z.object({
    documentExternalId: z.string(),
    sourcePayloadHash: z.string(),
    status: z.enum(["pending", "submitted", "completed", "skipped", "failed"]),
    filename: z.string().nullable(),
    contentType: z.string().nullable(),
    fileSizeBytes: z.number().nullable(),
    documentInput: z.string().nullable(),
    reductoJobId: z.string().nullable(),
    extractedText: z.string().nullable(),
    numPages: z.number().nullable(),
    credits: z.number().nullable(),
    studioLink: z.string().nullable(),
    pdfUrl: z.string().nullable(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    attempts: z.number().int(),
    submittedAt: z.number().nullable(),
    completedAt: z.number().nullable(),
    updatedAt: z.number(),
}).strict();
const extraction = workerOvertureDocumentExtractionSchema;
export const workerOvertureDocumentResponseSchema = z.discriminatedUnion("operation", [
    z.object({ operation: z.literal("open"), document: workerOvertureDocumentSchema, extraction: extraction.nullable() }).strict(),
    z.object({ operation: z.literal("read"), extraction: extraction.nullable() }).strict(),
    z.object({ operation: z.literal("claim"), claimed: extraction.nullable() }).strict(),
    z.object({ operation: z.literal("submitted") }).strict(),
    z.object({ operation: z.literal("completed") }).strict(),
    z.object({ operation: z.literal("skipped") }).strict(),
    z.object({ operation: z.literal("failed") }).strict(),
]);
/** Stable refusal codes; the executor turns each into the tool error the agent sees. */
export const WORKER_OVERTURE_DOCUMENT_REFUSALS = {
    leaseNotHeld: "overture_document_lease_not_held",
    documentNotFound: "overture_document_not_found",
    revisionChanged: "overture_document_revision_changed",
};
